import React, { useState } from 'react';
import { View, Text, Button } from "../../Components/Themed";
import type {
    RouterMarketProps,
    RouterMarketSideProps,
    RouterMarketVariableProps,
    RouterContestProps,
    RouterLeagueProps,
    RouterPartnerProps,
    PartnerMarketProps,
    BuyOpportunity
} from '../types';
import type { MyPlayerProps, GetContestResponse } from '../../types';
import { ScrollView, Modal } from 'react-native';
import { useColors } from '../../constants/useColors';
import BuyOpportunities from '../components/BuyOpportunities';
import MyOpportunities from '../components/MyOpportunities';
import OpportunityHistory from '../components/OpportunityHistory';
import ContestLiquidity from '../components/ContestLiquidity';
import AccountManager from '../components/AccountManager';
import ContestSelector from '../components/ContestSelector';
import moment from 'moment-mini';

type DesktopLayoutProps = {
    me: MyPlayerProps;
    partners: RouterPartnerProps[];
    leagues: RouterLeagueProps[];
    selectedLeague: RouterLeagueProps | null;
    contests: RouterContestProps[];
    filteredContests: RouterContestProps[];
    selectedContest: RouterContestProps | null;
    contestDetails: GetContestResponse | undefined;
    opportunities: {
        router_contests: RouterContestProps[];
        contests_processed: number;
        total_liquidity_count: number;
        total_opportunities: number;
        partner_markets: PartnerMarketProps[];
        router_market_variables: RouterMarketVariableProps[];
        router_markets: RouterMarketProps[];
        router_market_sides: RouterMarketSideProps[];
        partners: RouterPartnerProps[];
        opportunities: BuyOpportunity[];
    };
    opps_loading: boolean;
    size: { width: number; height: number };
    insets: { top: number; bottom: number; left: number; right: number };
    onSelectLeague: (league: RouterLeagueProps) => void;
    onSelectContest: (contest: RouterContestProps) => void;
    onOpenAdminPortal: (initialContestId?: string, initialLeagueId?: string) => void;
    onBuyOpportunity: (opportunity: BuyOpportunity, legIndex?: number) => void;
    onRefreshOpportunities?: () => Promise<void>;
};

const DesktopLayout = ({
    me,
    partners,
    leagues,
    selectedLeague,
    //contests,
    filteredContests,
    selectedContest,
    //contestDetails,
    opportunities,
    opps_loading,
    size,
    insets,
    onSelectLeague,
    onSelectContest,
    onOpenAdminPortal,
    onBuyOpportunity,
    onRefreshOpportunities
}: DesktopLayoutProps) => {
    const Colors = useColors();
    const [collapsedPanels, setCollapsedPanels] = useState<Set<string>>(new Set());
    //const [showContestModal, setShowContestModal] = useState(false);

    const togglePanel = (panelKey: string) => {
        const newSet = new Set(collapsedPanels);
        if (newSet.has(panelKey)) {
            newSet.delete(panelKey);
        } else {
            newSet.add(panelKey);
        }
        setCollapsedPanels(newSet);
    };

    const isCollapsed = (panelKey: string) => collapsedPanels.has(panelKey);

    return (
        <View style={{ flex: 1 }}>
            {/* Main 3-Column Dashboard */}
            <View style={{
                flex: 1,
                flexDirection: 'row'
            }}>
                {/* Left Column - 25% - My Bets + History */}
                <View style={{
                    width: isCollapsed('left') ? 50 : '25%',
                    minWidth: isCollapsed('left') ? 50 : 300,
                    borderRightWidth: 1,
                    borderRightColor: Colors.borders.light
                }}>
                    {isCollapsed('left') ? (
                        <View style={{ padding: 10, alignItems: 'center' }}>
                            <Button
                                type='text'
                                title='▶'
                                onPress={() => togglePanel('left')}
                                style={{ padding: 5 }}
                            />
                        </View>
                    ) : (
                        <View style={{ flex: 1 }}>
                            {/* Collapse button */}
                            <View transparent style={{
                                position: 'absolute',
                                top: 10,
                                right: 5,
                                zIndex: 10
                            }}>
                                <Button
                                    type='text'
                                    title='◀'
                                    onPress={() => togglePanel('left')}
                                    style={{ padding: 5, opacity: 0.5 }}
                                />
                            </View>

                            <ScrollView style={{ flex: 1 }}>
                                {/* Account Manager */}
                                <View float style={{ margin: 10 }}>
                                    <AccountManager
                                        me={me}
                                        onOpenAdminPortal={onOpenAdminPortal}
                                    />
                                </View>

                                {/* My Active Bets */}
                                <View float style={{ margin: 10, minHeight: 350 }}>
                                    <MyOpportunities
                                        partners={partners}
                                        compact={true}
                                        insets={insets}
                                        onBuyOpportunity={onBuyOpportunity}
                                    />
                                </View>

                                {/* History/Stats */}
                                <View float style={{ margin: 10, minHeight: 300 }}>
                                    <OpportunityHistory
                                        me={me}
                                        compact={true}
                                        insets={insets}
                                        partners={partners}
                                    />
                                </View>
                            </ScrollView>
                        </View>
                    )}
                </View>

                {/* Center Column - 50% - Buy Opportunities */}
                <View style={{
                    flex: 1,
                    minWidth: 400
                }}>
                    <View float style={{ margin: 10, flex: 1 }}>
                        <BuyOpportunities
                            loading={opps_loading}
                            router_markets={opportunities.router_markets}
                            router_market_sides={opportunities.router_market_sides}
                            router_market_variables={opportunities.router_market_variables}
                            partner_markets={opportunities.partner_markets}
                            buy_opportunities={opportunities.opportunities}
                            width={size.width}
                            partners={opportunities.partners}
                            router_contests={opportunities.router_contests}
                            insets={insets}
                            onBuyOpportunity={onBuyOpportunity}
                            onRefresh={onRefreshOpportunities}
                        />
                    </View>
                </View>

                {/* Right Column - 25% - Contest List */}
                <View style={{
                    width: isCollapsed('right') ? 50 : '25%',
                    minWidth: isCollapsed('right') ? 50 : 300,
                    borderLeftWidth: 1,
                    borderLeftColor: Colors.borders.light
                }}>
                    {isCollapsed('right') ? (
                        <View style={{ padding: 10, alignItems: 'center' }}>
                            <Button
                                type='text'
                                title='◀'
                                onPress={() => togglePanel('right')}
                                style={{ padding: 5 }}
                            />
                        </View>
                    ) : (
                        <View style={{ flex: 1 }}>
                            {/* Collapse button */}
                            <View transparent style={{
                                position: 'absolute',
                                top: 10,
                                left: 5,
                                zIndex: 10
                            }}>
                                <Button
                                    type='text'
                                    title='▶'
                                    onPress={() => togglePanel('right')}
                                    style={{ padding: 5, opacity: 0.5 }}
                                />
                            </View>

                            <ContestSelector
                                leagues={leagues}
                                selectedLeague={selectedLeague}
                                contests={filteredContests}
                                selectedContest={selectedContest}
                                onSelectLeague={onSelectLeague}
                                onSelectContest={onSelectContest}
                            />
                        </View>
                    )}
                </View>
            </View>

            {/* Contest Liquidity Modal */}
            {selectedContest && (
                <Modal
                    visible={true}
                    transparent={true}
                    animationType="fade"
                    onRequestClose={() => onSelectContest(null as any)}
                >
                    <View style={{
                        flex: 1,
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: 20
                    }}>
                        <View float style={{
                            width: '95%',
                            height: '90%',
                            borderRadius: 8,
                            overflow: 'hidden'
                        }}>
                            <View type='header' style={{
                                flexDirection: 'row',
                                alignItems: 'center',
                                padding: 15,
                                borderBottomWidth: 1,
                                borderBottomColor: Colors.borders.light
                            }}>
                                <View transparent style={{ flex: 1 }}>
                                    <Text theme='h1' style={{ fontSize: 18 }}>
                                        {selectedContest.contest_label}
                                    </Text>
                                    <Text theme='description' style={{ fontSize: 12, marginTop: 2 }}>
                                        {moment(selectedContest.scheduled_datetime).format('ddd, MMM D, YYYY [at] h:mm A')}
                                    </Text>
                                </View>
                                <Button
                                    type='text'
                                    title='✕ Close'
                                    onPress={() => onSelectContest(null as any)}
                                    style={{ padding: 8 }}
                                />
                            </View>
                            <ScrollView style={{ flex: 1 }}>
                                <ContestLiquidity
                                    router_contest_id={selectedContest.router_contest_id}
                                    include_opportunities={true}
                                    onBuyOpportunity={onBuyOpportunity}
                                    me={me}
                                    onOpenAdminPortal={onOpenAdminPortal}
                                />
                            </ScrollView>
                        </View>
                    </View>
                </Modal>
            )}
        </View>
    );
};

export default DesktopLayout;
